import { useMemo, useState } from 'react';
import type { TicketType } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { useServiceCategories } from '../service-categories/api.js';
import { useBillingClasses } from '../billing-classes/api.js';
import { TicketTypeFormModal } from './TicketTypeFormModal.js';
import { useTicketTypeGroups, useTicketTypes } from './api.js';

export function TicketTypesPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TicketType | null>(null);

  const query = useTicketTypes({ page, pageSize: DEFAULT_PAGE_SIZE, sortBy: 'name' });
  const groupsQuery = useTicketTypeGroups({ page: 1, pageSize: 200 });
  const categoriesQuery = useServiceCategories({ page: 1, pageSize: 200 });
  const billingClassesQuery = useBillingClasses({ page: 1, pageSize: 200 });

  const groupNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groupsQuery.data?.data ?? []) map.set(g.id, g.name);
    return map;
  }, [groupsQuery.data]);
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categoriesQuery.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [categoriesQuery.data]);
  const billingClassNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of billingClassesQuery.data?.data ?? []) map.set(b.id, b.name);
    return map;
  }, [billingClassesQuery.data]);

  const types = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (type: TicketType): void => {
    setEditing(type);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Ticket Types"
        description="Define support ticket types and their default service category & billing class."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="ticketTypes" className="h-4 w-4" />
              New ticket type
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Group</th>
              <th className="px-4 py-3 font-medium">Default category</th>
              <th className="px-4 py-3 font-medium">Default billing class</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={5}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={types.length === 0}
              emptyTitle="No ticket types found"
              emptyDescription="Create your first ticket type to get started."
              emptyAction={
                isAdmin ? <Button onClick={openCreate}>New ticket type</Button> : undefined
              }
            >
              {types.map((type) => (
                <tr
                  key={type.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(type)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{type.name}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {groupNames.get(type.groupId) ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {categoryNames.get(type.defaultServiceCategoryId) ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {billingClassNames.get(type.defaultBillingClassId) ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={type.isActive ? 'green' : 'gray'}>
                      {type.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && types.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <TicketTypeFormModal
        isOpen={formOpen}
        ticketType={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
